import axios from 'axios';
import { Component } from 'react';
import Cookie from 'react-cookies';
import { connect } from 'react-redux';

import { getShipmentManifests } from '../actions/ShipmentManifestActions';

import { Alert, Button, Dialog, DownloadIcon, EyeOpenIcon, LockIcon, Pane, PlusIcon, Table, TextInputField, TrashIcon, toaster } from 'evergreen-ui';
import DataTable from '../../common/DataTable/DataTable';

import Api from '../../../util/Api';
import Configuration from '../../../util/Configuration';
import ModuleConfig from '../Config';

const endpoints = ModuleConfig.Endpoints;

class ListShipmentManifests extends Component {
    constructor(props) {
        super(props);
        this.state = {
            addAwbOpen: false,
            awbValue: '',
            addAwbLoading: false,
            lastRegistered: null,

            closeOpen: false,
            closeLoading: false,
            closeTargetId: null,
            openManifestId: null,

            detailOpen: false,
            detailManifest: null
        };
    }

    componentDidMount() {
        this._findOpenManifest();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.data !== this.props.data) {
            this._findOpenManifest();
        }
    }

    _findOpenManifest() {
        const open = (this.props.data || []).find(m => m.status === 'open');
        this.setState({ openManifestId: open ? open.id : null });
    }

    handleTableChange = (opts) => {
        this.props.dispatch(getShipmentManifests(opts));
    }

    refreshTable = () => {
        this.props.dispatch(getShipmentManifests({ start: 0, limit: 20 }));
    }

    // ---- Add AWB ----
    openAddAwbDialog = () => {
        this.setState({ addAwbOpen: true, awbValue: '', lastRegistered: null });
    }

    closeAddAwbDialog = () => {
        this.setState({ addAwbOpen: false, awbValue: '', lastRegistered: null, addAwbLoading: false });
        this.refreshTable();
    }

    handleRegisterAwb = async () => {
        const { awbValue } = this.state;
        if (!awbValue.trim()) return;

        this.setState({ addAwbLoading: true });
        try {
            const result = await Api.post(endpoints.registerShipment, {}, {}, { awb: awbValue.trim() });
            const data = result.data;
            if (data.alreadyRegistered) {
                toaster.warning(`AWB ${awbValue.trim()} este deja inregistrat in manifest.`);
            } else {
                toaster.success(`AWB ${awbValue.trim()} a fost adaugat in manifest.`);
            }
            this.setState({
                awbValue: '',
                addAwbLoading: false,
                lastRegistered: data
            });
        } catch (err) {
            toaster.danger(`Eroare la inregistrare AWB: ${err.message || err}`);
            this.setState({ addAwbLoading: false });
        }
    }

    // ---- Close Manifest ----
    openCloseDialog = () => {
        this.setState({ closeOpen: true });
    }

    closeCloseDialog = () => {
        this.setState({ closeOpen: false, closeLoading: false });
    }

    handleCloseManifest = async () => {
        const { openManifestId } = this.state;
        if (!openManifestId) return;

        this.setState({ closeLoading: true });
        try {
            await Api.post(endpoints.closeManifest, { id: openManifestId }, {}, {});
            toaster.success(`Manifestul #${openManifestId} a fost inchis.`);
            this.setState({ closeOpen: false, closeLoading: false, openManifestId: null });
            this.refreshTable();
        } catch (err) {
            toaster.danger(`Eroare la inchiderea manifestului: ${err.message || err}`);
            this.setState({ closeLoading: false });
        }
    }

    handleCloseManifestById = async (manifestId) => {
        this.setState({ closeLoading: true, closeTargetId: manifestId });
        try {
            await Api.post(endpoints.closeManifest, { id: manifestId }, {}, {});
            toaster.success(`Manifestul #${manifestId} a fost inchis.`);
            this.setState({ closeLoading: false, closeTargetId: null });
            this.refreshTable();
        } catch (err) {
            toaster.danger(`Eroare la inchiderea manifestului: ${err.message || err}`);
            this.setState({ closeLoading: false, closeTargetId: null });
        }
    }

    // ---- Detail Modal ----
    openDetailModal = (manifest) => {
        this.setState({ detailOpen: true, detailManifest: manifest });
    }

    closeDetailModal = () => {
        this.setState({ detailOpen: false, detailManifest: null });
        this.refreshTable();
    }

    handleDeleteItem = async (manifestId, itemId) => {
        try {
            await Api.delete(endpoints.deleteItem, { manifestId, itemId });
            toaster.success('AWB sters din manifest.');
            // Update local state to remove the item without closing the modal
            const { detailManifest } = this.state;
            if (detailManifest) {
                const updatedItems = (detailManifest.items || []).filter(i => i.id !== itemId);
                this.setState({
                    detailManifest: { ...detailManifest, items: updatedItems, itemCount: updatedItems.length }
                });
            }
        } catch (err) {
            toaster.danger(`Eroare la stergerea AWB: ${err.message || err}`);
        }
    }

    // ---- PDF Download ----
    handleDownloadPdf = async (manifestId) => {
        try {
            const headers = { 'Content-Type': 'application/json' };
            const sessionCookie = Cookie.load('nax-session');
            if (sessionCookie) {
                headers.session = sessionCookie;
            }

            const response = await axios.get(
                `${Configuration.host}/v2/shipment-manifests/${manifestId}/pdf`,
                { headers, responseType: 'blob' }
            );

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `manifest-${manifestId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(a.href);
        } catch (err) {
            toaster.danger('Descarcarea PDF a esuat.');
            console.error('Failed to download manifest PDF:', err);
        }
    }

    render() {
        const { addAwbOpen, awbValue, addAwbLoading, lastRegistered, closeOpen, closeLoading, closeTargetId, openManifestId, detailOpen, detailManifest } = this.state;

        return (
            <>
                <Pane display="flex" alignItems="center" marginBottom={16}>
                    <Pane flex={1}>
                        <h2 style={{ margin: 0 }}>Manifeste Expediere</h2>
                    </Pane>
                    <Pane>
                        <Button
                            iconBefore={PlusIcon}
                            appearance="primary"
                            marginRight={8}
                            onClick={this.openAddAwbDialog}
                        >
                            Adauga AWB
                        </Button>
                        <Button
                            iconBefore={LockIcon}
                            intent="warning"
                            disabled={!openManifestId}
                            onClick={this.openCloseDialog}
                        >
                            Inchide Manifest #{openManifestId || '—'}
                        </Button>
                    </Pane>
                </Pane>

                {/* Add AWB Dialog */}
                <Dialog
                    isShown={addAwbOpen}
                    title="Adauga AWB in Manifest"
                    hasFooter={false}
                    onCloseComplete={this.closeAddAwbDialog}
                    onClose={this.closeAddAwbDialog}
                    shouldCloseOnOverlayClick={false}
                    width={500}
                >
                    <TextInputField
                        label="Numar AWB"
                        placeholder="Introdu numarul AWB"
                        value={awbValue}
                        onChange={(e) => this.setState({ awbValue: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') this.handleRegisterAwb(); }}
                        autoFocus
                    />
                    <Pane display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Button
                            appearance="primary"
                            isLoading={addAwbLoading}
                            disabled={!awbValue.trim()}
                            onClick={this.handleRegisterAwb}
                            marginRight={8}
                        >
                            Adauga
                        </Button>
                        <Button onClick={this.closeAddAwbDialog}>
                            Inchide
                        </Button>
                    </Pane>

                    {lastRegistered && lastRegistered.order && (
                        <Alert intent="none" title="Comanda gasita" marginTop={16}>
                            <strong>Nume:</strong> {lastRegistered.order.name}<br />
                            <strong>Valoare:</strong> {lastRegistered.order.value} RON<br />
                            <strong>Plata:</strong> {lastRegistered.order.paymentType || '—'}
                        </Alert>
                    )}

                    {lastRegistered && !lastRegistered.order && (
                        <Alert intent="warning" title="AWB adaugat" marginTop={16}>
                            Nu a fost gasita o comanda asociata acestui AWB.
                        </Alert>
                    )}
                </Dialog>

                {/* Close Manifest Confirm Dialog */}
                <Dialog
                    isShown={closeOpen}
                    title={`Inchide Manifestul #${openManifestId || ''}`}
                    intent="warning"
                    confirmLabel="Inchide Manifest"
                    cancelLabel="Anuleaza"
                    isConfirmLoading={closeLoading}
                    onConfirm={this.handleCloseManifest}
                    onCloseComplete={this.closeCloseDialog}
                    onClose={this.closeCloseDialog}
                >
                    Esti sigur ca vrei sa inchizi manifestul curent? Dupa inchidere nu se mai pot adauga colete.
                </Dialog>

                <DataTable
                    defaultFilter='id'
                    defaultDirection='desc'
                    columns={[
                        { id: 'id', label: 'Id' },
                        { id: 'date', label: 'Data', decorator: 'date' },
                        { id: 'status', label: 'Status', decorator: 'map', map: { open: 'Deschis', closed: 'Inchis' } },
                        { id: 'itemCount', label: 'Nr. Colete' },
                        { id: 'createdAt', label: 'Creat', decorator: 'date' },
                        {
                            id: 'details',
                            label: 'Detalii',
                            decorator: (item) => (
                                <Button
                                    iconBefore={EyeOpenIcon}
                                    appearance="minimal"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.openDetailModal(item);
                                    }}
                                >
                                    Detalii
                                </Button>
                            )
                        },
                        {
                            id: 'actions',
                            label: 'Actiuni',
                            decorator: (item) => (
                                item.status === 'closed' ? (
                                    <Button
                                        iconBefore={DownloadIcon}
                                        appearance="minimal"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.handleDownloadPdf(item.id);
                                        }}
                                    >
                                        PDF
                                    </Button>
                                ) : (
                                    <Button
                                        iconBefore={LockIcon}
                                        appearance="minimal"
                                        size="small"
                                        intent="warning"
                                        isLoading={closeTargetId === item.id && closeLoading}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.handleCloseManifestById(item.id);
                                        }}
                                    >
                                        Inchide
                                    </Button>
                                )
                            )
                        }
                    ]}
                    data={this.props.data}
                    updateKey={this.props.data}
                    page={this.props.page}
                    pageSize={this.props.pageSize}
                    pageCount={this.props.pageCount}
                    onTableChange={this.handleTableChange}
                />

                {/* Detail Modal — list of tracking numbers */}
                <Dialog
                    isShown={detailOpen}
                    title={detailManifest ? `Manifest #${detailManifest.id} — Colete` : 'Detalii Manifest'}
                    hasFooter={false}
                    onCloseComplete={this.closeDetailModal}
                    onClose={this.closeDetailModal}
                    width={600}
                >
                    {detailManifest && detailManifest.items && detailManifest.items.length > 0 ? (
                        <Table>
                            <Table.Head>
                                <Table.TextHeaderCell>#</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Tracking Number</Table.TextHeaderCell>
                                <Table.TextHeaderCell>ID Comanda</Table.TextHeaderCell>
                                {detailManifest.status === 'open' && (
                                    <Table.TextHeaderCell width={80}>Sterge</Table.TextHeaderCell>
                                )}
                            </Table.Head>
                            <Table.Body>
                                {detailManifest.items.map((ti, idx) => (
                                    <Table.Row key={ti.id || idx}>
                                        <Table.TextCell>{idx + 1}</Table.TextCell>
                                        <Table.TextCell>{ti.trackingNumber}</Table.TextCell>
                                        <Table.TextCell>{ti.orderId || '—'}</Table.TextCell>
                                        {detailManifest.status === 'open' && (
                                            <Table.TextCell width={80}>
                                                <Button
                                                    iconBefore={TrashIcon}
                                                    appearance="minimal"
                                                    intent="danger"
                                                    size="small"
                                                    onClick={() => this.handleDeleteItem(detailManifest.id, ti.id)}
                                                />
                                            </Table.TextCell>
                                        )}
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    ) : (
                        <Pane padding={16}>Nu exista colete in acest manifest.</Pane>
                    )}
                </Dialog>
            </>
        );
    }
}

function mapStateToProps(state) {
    return state.shipmentManifests;
}

export default connect(mapStateToProps)(ListShipmentManifests);
